import {
  EnvironmentOutlined,
  GlobalOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import React from "react";
import styled from "styled-components";

const LocationChoicesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background: white;
  height: 100%;
  width: 100%;
  max-width: 380px;
  overflow-y: auto;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 9;
  padding-top: 60px;

  @media (max-width: 992px) {
    bottom: 0;
    top: auto;
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding-top: 0;
    height: fit-content;
    width: 100%;

    @media screen and (max-width: 767px) {
      padding-bottom: 80px;
    }
  }
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Title = styled.div`
  margin: 0;
  font-size: 18px;
  color: #1a1a1a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const LocationList = styled.div`
  padding: 8px;
    
  @media screen and (max-width: 767px) {
    max-height: calc(100dvh - 198px);
    overflow: auto;
    border-bottom: 1px solid #e8e8e8;
  }
`;

const LocationItem = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 8px;
  margin-bottom: 4px;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LocationName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LocationAddress = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const LocationActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  font-size: 13px;
  height: 32px;
  padding: 0 12px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
`;

interface LocationChoice {
  ref_id: string;
  display: string;
}

interface LocationChoicesListProps {
  locationChoices: LocationChoice[];
  onLocationSelect: (location: LocationChoice) => void;
  onClose?: () => void;
}

const LocationChoicesList: React.FC<LocationChoicesListProps> = ({
  locationChoices,
  onLocationSelect,
  onClose,
}) => {
  if (!locationChoices || locationChoices.length === 0) {
    return null;
  }

  const handleLocationClick = (location: LocationChoice) => {
    onLocationSelect(location);
  };

  const handleGetDirections = (
    e: React.MouseEvent,
    location: LocationChoice
  ) => {
    e.stopPropagation();
    onLocationSelect(location);
  };

  const handleViewWebsite = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement website view if available
  };

  // Parse display text to extract name and address
  const parseLocationDisplay = (display: string) => {
    const parts = display.split(",");
    if (parts.length > 0) {
      return {
        name: parts[0].trim(),
        address: parts.slice(1).join(", ").trim(),
      };
    }
    return {
      name: display,
      address: "",
    };
  };

  return (
    <LocationChoicesContainer>
      <Header>
        <Title>Kết quả tìm kiếm ({locationChoices.length})</Title>
        {onClose && (
          <CloseButton onClick={onClose} aria-label="Đóng">
            ×
          </CloseButton>
        )}
      </Header>
      <LocationList>
        {locationChoices.map((location, index) => {
          const { name, address } = parseLocationDisplay(location.display);
          return (
            <LocationItem
              key={location.ref_id || index}
              onClick={() => handleLocationClick(location)}
            >
              <LocationName>
                <EnvironmentOutlined style={{ color: "#1890ff" }} />
                {name}
              </LocationName>
              {address && <LocationAddress>{address}</LocationAddress>}
              <LocationActions>
                <ActionButton
                  type="default"
                  icon={<RightOutlined />}
                  onClick={(e) => handleGetDirections(e, location)}
                >
                  Đường đi
                </ActionButton>
                <ActionButton
                  type="default"
                  icon={<GlobalOutlined />}
                  onClick={handleViewWebsite}
                  style={{ display: "none" }} // Hide for now until we have website URLs
                >
                  Trang web
                </ActionButton>
              </LocationActions>
            </LocationItem>
          );
        })}
      </LocationList>
    </LocationChoicesContainer>
  );
};

export default LocationChoicesList;
